import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const WorkspaceMergeContext = createContext(null);

const DEFAULT_FIELDS = {
  firstName: '',
  lastName: '',
  dob: '',
  beneficiary: '',
  carrierName: '',
  coverageAmount: '',
  premium: '',
  agentName: '',
  npn: '',
};

function parseNum(raw) {
  if (raw == null || raw === '') return 0;
  const cleaned = String(raw).replace(/[$,]/g, '');
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

export function WorkspaceMergeProvider({ children }) {
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [intake_data, setIntakeData] = useState({});
  const [language, setLanguageState] = useState(() => {
    try { return localStorage.getItem('workspace_language_preference') || 'en'; }
    catch { return 'en'; }
  });

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    try { localStorage.setItem('workspace_language_preference', lang); } catch {}
  }, []);

  const setMergeField = useCallback((key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const bulkSetFields = useCallback((partial) => {
    setFields((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetFields = useCallback(() => {
    setFields(DEFAULT_FIELDS);
  }, []);

  const setIntakeField = useCallback((key, value) => {
    setIntakeData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const bulkSetIntakeFields = useCallback((partial) => {
    setIntakeData((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetIntakeData = useCallback(() => {
    setIntakeData({});
  }, []);

  useEffect(() => {
    const mortgageBalance = parseNum(intake_data.mortgageBalance);
    const carLoans = parseNum(intake_data.carLoans);
    const creditCards = parseNum(intake_data.creditCards);
    const otherDebt = parseNum(intake_data.otherDebt);
    const totalDebt = mortgageBalance + carLoans + creditCards + otherDebt;

    const annualIncome = parseNum(intake_data.annualIncome);
    const incomeYears = parseNum(intake_data.incomeYears);
    const incomeReplacement = parseNum(intake_data.incomeReplacement) || (annualIncome * (incomeYears || 10));

    const numberOfKids = parseNum(intake_data.numberOfKids);
    const educationPerChild = parseNum(intake_data.educationPerChild);
    const educationTotal = parseNum(intake_data.educationTotal) || (numberOfKids * (educationPerChild || 50000));

    const fexCoverageAmount = parseNum(intake_data.fexCoverageAmount);
    const isFexPath = fexCoverageAmount > 0 && totalDebt === 0 && incomeReplacement === 0;

    const totalInsurableNeed = isFexPath
      ? fexCoverageAmount
      : totalDebt + incomeReplacement + educationTotal;

    if (totalInsurableNeed > 0) {
      const formatted = '$' + Math.round(totalInsurableNeed).toLocaleString('en-US');
      setMergeField('coverageAmount', formatted);
    }
  }, [intake_data, setMergeField]);

  useEffect(() => {
    if (intake_data.beneficiary) {
      setMergeField('beneficiary', intake_data.beneficiary);
    }
  }, [intake_data.beneficiary, setMergeField]);

  return (
    <WorkspaceMergeContext.Provider value={{
      fields, setMergeField, bulkSetFields, resetFields,
      intake_data, setIntakeField, bulkSetIntakeFields, resetIntakeData,
      language, setLanguage,
    }}>
      {children}
    </WorkspaceMergeContext.Provider>
  );
}

export function useWorkspaceMerge() {
  const ctx = useContext(WorkspaceMergeContext);
  if (!ctx) throw new Error('useWorkspaceMerge must be used within WorkspaceMergeProvider');
  return ctx;
}
